import { z } from 'zod';
import { PasskeyAuthenticationResponse, PasskeyRequestOptions, StepUpFactorCode } from './auth.js';
import { PersonRef } from './people.js';
/**
 * Workspace export and deletion (docs/adr/0023-export-and-deletion.md; brief: "data export and
 * workspace deletion", owner only, with step-up). Both ask for a fresh six-digit code emailed to the
 * owner first.
 */
/**
 * `support_access`: approving break-glass support access (docs/adr/0051). `personal_export`: anyone downloading
 * their own data (docs/adr/0056-personal-data-export.md). `sso`: owners changing single sign-on, domains or SCIM
 * (docs/adr/0055); the code opens a five-minute window for this device session instead of being spent on one change.
 */
export const StepUpPurpose = z.enum(['export', 'delete', 'support_access', 'personal_export', 'sso']);
export const StepUpInput = z.object({ purpose: StepUpPurpose });
export const StepUpResponse = z.object({ expiresAt: z.string() });
/**
 * Step-up with a passkey (docs/adr/0026-passkeys.md): a person who has one confirms with it instead of an
 * emailed code. A verified assertion gives a one-time six-digit code, valid five minutes, which the client
 * sends as the usual `code` to export or delete. Those routes are unchanged and the code works once.
 */
export const PasskeyStepUpOptions = z.object({ challengeId: z.uuid(), options: PasskeyRequestOptions });
export const PasskeyStepUpInput = z.object({ challengeId: z.uuid(), response: PasskeyAuthenticationResponse });
export const PasskeyStepUpResponse = z.object({ code: z.string().regex(/^\d{6}$/), expiresAt: z.string() });
/**
 * Step-up with an authenticator app or a recovery code (security review follow-up, 16 Oct 2026): owners who have
 * two-factor sign-in confirm with it instead of an emailed code, and get the same one-time code as a passkey gives.
 */
export const AuthenticatorStepUpInput = z.object({ purpose: StepUpPurpose, method: z.enum(['totp', 'recovery_code']), code: StepUpFactorCode });
const Code = z.string().regex(/^\d{6}$/, 'Enter the six-digit code from the email');
export const ExportStatus = z.enum(['queued', 'running', 'ready', 'failed', 'expired']);
export const WorkspaceExport = z.object({
    id: z.uuid(),
    status: ExportStatus,
    includeFiles: z.boolean(),
    /** Whether direct messages and private channels are in the copy (off unless the owner chose it). */
    includePrivate: z.boolean(),
    requestedBy: PersonRef.nullable(),
    sizeBytes: z.number().int().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
    /** Ready exports can be downloaded until then (7 days), then they are deleted. */
    expiresAt: z.string().nullable(),
});
export const ExportList = z.object({ items: z.array(WorkspaceExport) });
/**
 * `includePrivate` (Nathan decided 17 Oct 2026, docs/adr/0023): direct messages and private channels, with their
 * reactions, pins, files and the Chapi conversations that drew on them, are left out unless the owner opts in here.
 * When they do, everyone whose private conversations are in the copy is told, in the app and by email.
 * Optional so a client from the previous release, which never sends it, gets the safe default.
 */
export const CreateExportInput = z.object({ includeFiles: z.boolean(), includePrivate: z.boolean().default(false), code: Code });
/** A short-lived link to the zip (one hour). */
export const ExportDownload = z.object({ url: z.string() });
export const DeleteWorkspaceInput = z.object({
    /** The workspace's name, typed exactly, so it cannot happen by accident. */
    confirmName: z.string().max(120),
    code: Code,
});
export const DeleteWorkspaceResponse = z.object({ deletionScheduledAt: z.string() });
/** Days between scheduling deletion and erasing the workspace for good; owners can restore it until then. */
export const DELETION_GRACE_DAYS = 30;
/** Days a finished export can be downloaded. */
export const EXPORT_KEEP_DAYS = 7;
// --- Personal data export (docs/adr/0056-personal-data-export.md) -----------------------------------
/** Hours a finished personal export can be downloaded (the emailed link says so). */
export const PERSONAL_EXPORT_KEEP_HOURS = 24;
/** One personal export per person per workspace in this many hours (failed ones don't count). */
export const PERSONAL_EXPORT_EVERY_HOURS = 24;
export const PersonalExport = z.object({
    id: z.uuid(),
    status: ExportStatus,
    sizeBytes: z.number().int().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    finishedAt: z.string().nullable(),
    /** Ready exports can be downloaded until then (24 hours), then they are deleted. */
    expiresAt: z.string().nullable(),
});
export const PersonalExportList = z.object({
    items: z.array(PersonalExport),
    /** When the next export can be asked for; null when it can be now. */
    nextAllowedAt: z.string().nullable(),
});
export const CreatePersonalExportInput = z.object({ code: Code });
//# sourceMappingURL=data.js.map